import { Link } from "react-router-dom";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import { Utensils, Store, ShoppingCart, Cigarette, Shirt, Scissors, Wrench, Globe, ArrowRight, Check, ChevronRight } from "lucide-react";
import CTASection from "@/components/CTASection";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import Illustrations from "@/components/illustrations/FintechIllustrations";
import IndustryIllustrations from "@/components/illustrations/IndustryIllustrations";

const verticals = [
  { id: "restaurants", icon: Utensils, t: "Restaurants", d: "Tableside ordering, kitchen tickets, tips, and split checks. Built for the rush.", to: "/industries/restaurants" },
  { id: "delis", icon: Store, t: "Delis & Convenience Stores", d: "Fast scanning, age verification, lottery & deli scale integration ready.", to: "/industries/convenience-stores-delis" },
  { id: "grocery", icon: ShoppingCart, t: "Grocery Stores", d: "EBT-ready POS, multi-lane setups, and produce-scale support.", to: "/industries/grocery-stores" },
  { id: "smoke-shops", icon: Cigarette, t: "Smoke Shops", d: "Age verification, scan data programs, cash discount-friendly setups.", to: "/industries/smoke-shops" },
  { id: "retail", icon: Shirt, t: "Retail Stores", d: "Inventory, variants, barcoding, gift cards, and loyalty out of the box.", to: "/industries/retail-stores" },
  { id: "salons", icon: Scissors, t: "Salons & Spas", d: "Service catalogs, tip pooling, online booking integrations.", to: "/industries/salons-spas" },
  { id: "service", icon: Wrench, t: "Service Businesses", d: "Quote-to-invoice flows, recurring billing, mobile payments in the field.", to: "/industries/service-businesses" },
  { id: "ecommerce", icon: Globe, t: "E-Commerce Businesses", d: "Hosted checkout, cart plugins, recurring billing, and chargeback help.", to: "/e-commerce" },
];

export default function Industries() {
  return (
    <div data-testid="industries-page">
      <Seo {...getSeoForPath("/industries")} />

      {/* ── Unified Hero ───────────────────────────────── */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)" }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)" }} />
        <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
        <ParticleField density={36} connectDistance={130} className="absolute inset-0 w-full h-full" />

        <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">Industries</span>
          </nav>

          <div className="grid lg:grid-cols-[1.15fr,1fr] gap-10 lg:gap-12 items-center">
            <Reveal>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                  Industries
                </div>
                <h1 className="font-display-tight text-[2rem] sm:text-[2.75rem] lg:text-[3.5rem] xl:text-[3.75rem] font-extrabold leading-[1.05] text-white tracking-[-0.02em]">
                  Built for the businesses that keep the country moving.
                </h1>
                <p className="mt-6 text-[16px] md:text-[17px] text-white/75 leading-[1.7] max-w-xl">
                  Every industry has its own quirks. Our setups, hardware, and software are tuned to the realities of how each one actually operates — from restaurants and retail to convenience stores and service businesses.
                </p>
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <Illustrations.EcosystemIllustration
                  transparent
                  className="relative w-full h-auto max-w-[520px] mx-auto drop-shadow-[0_30px_60px_rgba(0,0,0,0.35)]"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section container-swipe">
        <div className="grid md:grid-cols-2 gap-6">
          {verticals.map((v) => {
            const Icon = v.icon;
            const Illus = IndustryIllustrations[v.id];
            return (
              <div
                key={v.id}
                id={v.id}
                className="group bg-white border border-line rounded-2xl overflow-hidden scroll-mt-32 flex flex-col transition-all duration-300 ease-out hover:-translate-y-1 hover:border-[var(--swipe-accent)] hover:shadow-[0_18px_44px_-18px_rgba(0,114,255,0.55)]"
                data-testid={`industry-card-${v.id}`}
              >
                <Link to={v.to} className="block relative w-full aspect-[36/11] overflow-hidden" aria-label={`${v.t} — see solutions`}>
                  {Illus ? <Illus /> : null}
                </Link>
                <div className="p-7 flex-1 flex flex-col">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[var(--swipe-muted)] text-[var(--swipe-navy)] flex items-center justify-center shadow-sm group-hover:bg-[var(--swipe-accent)] group-hover:text-white transition-colors duration-300">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h2 className="font-display text-xl font-black text-[var(--swipe-navy)] leading-tight">
                      <Link to={v.to} className="hover:text-[var(--swipe-accent)] transition-colors">{v.t}</Link>
                    </h2>
                  </div>
                  <p className="mt-4 text-slate-600 leading-relaxed">{v.d}</p>
                  <div className="mt-auto pt-5 flex flex-wrap gap-x-5 gap-y-2">
                    <Link to={v.to} className="text-sm font-semibold text-[var(--swipe-accent)] hover:underline inline-flex items-center gap-1.5" data-testid={`industry-card-link-${v.id}`}>
                      See solutions <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link to="/savings-analysis" className="text-sm font-semibold text-[var(--swipe-navy)] hover:text-[var(--swipe-accent)] inline-flex items-center gap-1.5">
                      Get a quote <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 bg-[var(--swipe-muted)] rounded-2xl p-8 lg:p-12 grid lg:grid-cols-[1.4fr,1fr] gap-8 items-center">
          <div>
            <h3 className="font-display-tight text-2xl sm:text-3xl font-black text-[var(--swipe-navy)]">Don&apos;t see your industry?</h3>
            <p className="mt-3 text-slate-600 leading-relaxed">
              We support most business types. Tell us what you run and we&apos;ll put a custom-fit setup together.
            </p>
          </div>
          <div className="flex lg:justify-end gap-3 flex-wrap">
            <Link to="/contact" className="btn-primary-swipe">Tell us about your business <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
